import * as XLSX from "xlsx";

export const IMPORT_FILE_TYPES = ".xlsx,.xls,.csv";

const extensionOf = file => (file?.name?.match(/\.([^.]+)$/)?.[1] || "").toLowerCase();
const cleanHeading = value => String(value ?? "")
  .replace(/^\uFEFF/, "")
  .replace(/[\u00a0\u202f]/g, " ")
  .replace(/[‐‑‒–—―]/g, "-")
  .replace(/\s+/g, " ")
  .trim()
  .toLocaleLowerCase("de-CH");
const display = value => String(value ?? "").replace(/^\uFEFF/, "").trim() || "(leer)";
const rowEmpty = row => row.every(value => String(value ?? "").trim() === "");

function contractOf(contract) {
  if (Array.isArray(contract)) return { columns: contract, headerVariants: [contract] };
  const columns = contract?.columns || [];
  return { columns, headerVariants: contract?.headerVariants?.length ? contract.headerVariants : [columns] };
}

function validateAndMapHeadings(actual, contract, location) {
  const { columns, headerVariants } = contractOf(contract);
  const aliases = new Map();
  headerVariants.forEach(variant => variant.forEach((heading, index) => {
    const key = cleanHeading(heading);
    const prior = aliases.get(key);
    if (prior !== undefined && prior !== index) throw new Error(`Importvertrag ist mehrdeutig: "${heading}".`);
    aliases.set(key, index);
  }));
  const mapped = actual.map(heading => aliases.get(cleanHeading(heading)));
  const unknown = actual.flatMap((heading, index) => mapped[index] === undefined ? [`Spalte ${index + 1} "${display(heading)}"`] : []);
  const duplicates = mapped.flatMap((target, index) => target !== undefined && mapped.indexOf(target) !== index ? [`"${display(actual[index])}"`] : []);
  // "Internes Projekt" was added after the original templates had shipped.
  // An omitted final column is therefore a supported legacy input and maps to false.
  const missing = columns.filter((_, index) => !mapped.includes(index) && index !== 10);
  const differences = [
    unknown.length && `unbekannt: ${unknown.join(", ")}`,
    duplicates.length && `doppelt: ${duplicates.join(", ")}`,
    missing.length && `fehlt: ${missing.join(", ")}`,
  ].filter(Boolean);
  if (differences.length) throw new Error(`${location}: Spaltenabweichung (${differences.join("; ")}).`);
  return mapped;
}

function swissNumber(value) {
  if (typeof value === "number" || value === "") return value;
  const text = String(value).trim();
  if (!/^[+-]?(?:\d{1,3}(?:['’\u00a0\u202f ]\d{3})+|\d+)(?:[.,]\d+)?$/.test(text)) return value;
  return text.replace(/['’\u00a0\u202f ]/g, "").replace(",", ".");
}

function swissDate(value) {
  if (typeof value !== "string") return value;
  const match = value.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!match) return value;
  const [, day, month, year] = match, date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (date.getUTCFullYear() !== Number(year) || date.getUTCMonth() !== Number(month) - 1 || date.getUTCDate() !== Number(day)) return value;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

function normalizeRows(values, mapping) {
  while (values.length && rowEmpty(values[values.length - 1])) values.pop();
  return values.filter(row => !rowEmpty(row)).map(row => {
    const normalized = Array(mapping.length).fill("");
    mapping.forEach((target, source) => {
      const value = row[source] ?? "";
      normalized[target] = [7, 8].includes(target) ? swissNumber(value) : value instanceof Date ? value.toISOString().slice(0, 10) : swissDate(value);
    });
    return normalized;
  });
}

function parseWorkbook(bytes, extension, contract) {
  let workbook;
  try { workbook = XLSX.read(bytes, { type: "array", raw: true, cellDates: true }); }
  catch (error) { throw new Error(`${extension.toUpperCase()}-Datei: nicht lesbar (${error.message}).`); }
  const sheetName = workbook.SheetNames[0], sheet = workbook.Sheets[sheetName];
  if (!sheet) throw new Error(`${extension.toUpperCase()}-Datei: kein lesbares Tabellenblatt.`);
  const values = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: true });
  const mapping = validateAndMapHeadings(values[0] || [], contract, `${extension.toUpperCase()}-Datei, Blatt "${sheetName}"`);
  return normalizeRows(values.slice(1), mapping);
}

function decodeCsv(bytes) {
  const data = new Uint8Array(bytes);
  if (data[0] === 0xff && data[1] === 0xfe) return new TextDecoder("utf-16le").decode(data.subarray(2));
  if (data[0] === 0xfe && data[1] === 0xff) {
    const swapped = data.slice(2); for (let index = 0; index + 1 < swapped.length; index += 2) [swapped[index], swapped[index + 1]] = [swapped[index + 1], swapped[index]];
    return new TextDecoder("utf-16le").decode(swapped);
  }
  try { return new TextDecoder("utf-8", { fatal: true }).decode(data).replace(/^\uFEFF/, ""); }
  catch { return new TextDecoder("windows-1252").decode(data).replace(/^\uFEFF/, ""); }
}

function delimiterOf(text) {
  const firstLine = text.split(/\r?\n/, 1)[0];
  const counts = [";", ",", "\t"].map(delimiter => [delimiter, firstLine.split(delimiter).length - 1]);
  counts.sort((a, b) => b[1] - a[1]);
  if (!counts[0][1] || counts[0][1] === counts[1][1]) throw new Error("CSV-Datei, Zeile 1: Trennzeichen konnte nicht eindeutig erkannt werden (erwartet ;, , oder Tabulator).");
  return counts[0][0];
}

function parseCsv(text, contract) {
  const delimiter = delimiterOf(text), rows = []; let row = [], field = "", quoted = false, line = 1;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (quoted) {
      if (character === '"' && text[index + 1] === '"') { field += '"'; index += 1; }
      else if (character === '"') quoted = false;
      else { field += character; if (character === "\n") line += 1; }
    } else if (character === '"' && field === "") quoted = true;
    else if (character === delimiter) { row.push(field); field = ""; }
    else if (character === "\n" || character === "\r") {
      if (character === "\r" && text[index + 1] === "\n") index += 1;
      row.push(field); rows.push(row); row = []; field = ""; line += 1;
    } else field += character;
  }
  if (quoted) throw new Error(`CSV-Datei, Zeile ${line}: nicht geschlossenes Anführungszeichen.`);
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  const mapping = validateAndMapHeadings(rows[0] || [], contract, "CSV-Datei, Zeile 1");
  return normalizeRows(rows.slice(1), mapping);
}

export async function readMandateImportFile(file, contract) {
  const extension = extensionOf(file);
  if (!extension || !["xlsx", "xls", "csv"].includes(extension)) throw new Error(`Dateityp ".${extension || "?"}" wird nicht unterstützt (erlaubt: XLSX, XLS, CSV).`);
  const bytes = await file.arrayBuffer();
  return extension === "csv" ? parseCsv(decodeCsv(bytes), contract) : parseWorkbook(bytes, extension, contract);
}

export function downloadMandateTemplate(template) {
  const worksheet = XLSX.utils.aoa_to_sheet([template.columns, template.example]);
  worksheet["!cols"] = template.columns.map(() => ({ wch: 24 }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, template.sheet);
  XLSX.writeFile(workbook, "Abelia_Projekte_Import_Vorlage.xlsx");
}
